import { createSlice } from '@reduxjs/toolkit'
import {apiUrl, bxUrl} from '@/api/axios.js';
import saveDataToLocalStorage, {setLocalStorage} from '../../core/utils/saveDataToLocalStorage.js';
import {handleResponseError} from '@/core/utils/handleResponseError.js';
import {
    checkIfLocalStorageDoesNotContainCurrentState
} from '@/core/utils/checkIfLocalStorageDoesNotContainCurrentState.js';
import {getTranslation} from '@/core/utils/i18nHelper';
import {resetResult} from '@/store/reducers/resultSlice';

const initialState = {
    testData: [],
    testId: null,
    answers: [],
    background: null,
    additional: {
        //todo реализована только реклама в блоках как conditional
        blocks: {
            "processProgressCounter": {
                "show": false,
                "content": ""
            },
            "processCloseBtn": {
                "show": false,
                "content": ""
            },
            "processProgressBar": {
                "show": false,
                "content": ""
            },
            "processLike": {
                "show": false,
                "content": ""
            },
            "processReportForm": {
                "show": false,
                "content": ""
            },
            "processAdvFullscreen": {
                "show": false,
                "content": ""
            },
            "processAdvBottom": {
                "show": false,
                "content": ""
            }
        },
        device: {
            country: '', // страна пользователя
        },
    },

    activeQuestion: 0,

    answerState: {
        isSelected: false,
    },

    localStorageKey: '',

    isFetching: true,
    isFinished: false,

    successMessage: null,
    error: null,

    isFormValid: false,
    validityDataKey: '',
    isTesting: false,
}

const testingSlice = createSlice({
    name: 'testing',
    initialState,
    reducers: {
        setTestId(state, action) {
            state.testId = action.payload
        },

        setIsTesting(state, action) {
            state.isTesting = action.payload
        },

        setBackground(state, action) {
            state.background = action.payload
        },

        setAdditional(state, action) {
            state.additional = action.payload
        },

        setLocalStorageKey(state, action) {
            state.localStorageKey = action.payload
        },

        setActiveQuestion(state, action) {
            state.activeQuestion = action.payload
        },

        setSelected(state, action) {
            state.answerState.isSelected = action.payload
        },

        setIsFinished(state, action) {
            state.isFinished = action.payload
        },

        fetchTestingStart(state) {
            state.isFetching = true
        },

        fetchTestingError(state, action) {
            state.isFetching = false
            state.error = action.payload
        },

        fetchTestingLoaded(state, action) {
            state.testData = [...action.payload]
            state.isFetching = false
        },

        updateAnswers(state, action) {
            state.answers = [...action.payload]
        },

        resetAnswers(state) {
            state.answers = []
        },

        resultAdded(state, action) {
            state.result = action.payload
        },

        answersSent(state, action) {
            state.successMessage = action.payload;
            state.error = null;
            state.isTesting = false;
        },

        setError(state, action) {
            state.error = action.payload;
        },

        resetTestingState(state) {
            state.answers = [];
            state.testData = [];
            // state.testId = null;
            state.activeQuestion = 0;
            state.answerState.isSelected = false;
            state.error = null;
            state.isFinished = false;
            state.successMessage = null;
        },

        setFormValidity(state, action) {
            state.isFormValid = action.payload
        },

        setValidityKey(state, action) {
            state.validityDataKey = action.payload
        },
    }
})

export const {
    setIsFinished,
    fetchTestingStart,
    fetchTestingLoaded,
    fetchTestingError,
    setActiveQuestion,
    setSelected,
    updateAnswers,
    resetAnswers,
    setTestId,
    setLocalStorageKey,
    resultAdded,
    updateLastAnswerTimestamp,
    answersSent,
    setError,
    setBackground,
    resetTestingState,
    setAdditional,
    setFormValidity,
    setValidityKey,
    setIsTesting,

} = testingSlice.actions

export default testingSlice.reducer

//for checking last invalid answer to go when we click continue testing
export const saveValidity = (isValid) => async (dispatch, getState) => {
    const {validityDataKey, activeQuestion} = getState().testing;

    const validityData = {
        activeQuestion: activeQuestion,
        isValid: isValid
    }
    setLocalStorage(validityDataKey, validityData);
}


export const extractTestId = (elementId) => (dispatch) => {
    const parentDiv = document.getElementById(elementId);

    if (parentDiv) {
        const dataTestIdStr = parentDiv.getAttribute('data-test-id');
        if (dataTestIdStr) {
            const dataTestId = Number(dataTestIdStr);
            if (!isNaN(dataTestId)) {
                dispatch(setTestId(dataTestId));
                return dataTestId;
            }
        }
    }

    console.error(`Test ID not found in element with ID "${elementId}"`);
    return null;
};

export const getTest = () => (dispatch, getState) => {

    const testId = dispatch(extractTestId('test-process'));

    if (testId) {
        const answersDataKey = `test_${testId}_answers`;
        dispatch(setLocalStorageKey(answersDataKey));

        dispatch(fetchTestData(testId, answersDataKey));
        dispatch(fetchTestBackground(testId));
    }
}

export const fetchTestData = (testId, localStorageKey) => async (dispatch) => {
    dispatch(fetchTestingStart())
    try {
        const response = await apiUrl.get(`api/questions?in_process=1&filter[uf_id_test]=${testId}`, { withCredentials: true });
        const testData = response.data.data;
        dispatch(fetchTestingLoaded(testData));
        dispatch(checkLocalStorage(localStorageKey))
    } catch (error) {
        handleResponseError(error, (data) => dispatch(fetchTestingError(data)));
    }
}

export const fetchTestBackground = (testId) => async (dispatch) => {
    try {
        const response = await apiUrl.get(`api/tests/${testId}`);
        const background = response.data.data.theme;
        const additional = response.data.data.additional;
        dispatch(setBackground(background));
        dispatch(setAdditional(additional));
    } catch (error) {
        handleResponseError(error, (data) => dispatch(fetchTestingError(data)));
    }
}

//check if we have answers in local storage then set active question
export function checkLocalStorage(localStorageKey) {
    return (dispatch, getState) => {
        const {testData, validityDataKey } = getState().testing;
        const storedAnswers = JSON.parse(localStorage.getItem(localStorageKey)) || [];
        const storedValidity = JSON.parse(localStorage.getItem(validityDataKey)) || [];

        if (storedAnswers.length > 0) {
            if (storedValidity.isValid === false) {
                dispatch(setActiveQuestion(storedValidity.activeQuestion))
            } else {
                if (storedAnswers.length === testData.length) {
                    // if we finished but couldn't send data last time
                    dispatch(setActiveQuestion(storedAnswers.length - 1))
                } else {
                    dispatch(setActiveQuestion(storedAnswers.length))
                }
            }
            dispatch(updateAnswers(storedAnswers));
        }
    }
}

export function answerClick(val, question_id, currentQuestionNextBtnShow, type = 'radio') {
    return (dispatch, getState) => {
        const state = getState().testing;
        const { answers, activeQuestion, localStorageKey } = getState().testing;
        checkIfLocalStorageDoesNotContainCurrentState(localStorageKey, answers)
        let answersArray = [];
        // find answers, excluding { name: null, val: null }
        const filteredAnswers = answers[activeQuestion]?.answer?.filter(answer => answer.name !== null);

        switch (type) {
            case 'radio': {
                const newRadio = { name: 0, val };
                answersArray = [newRadio];
                break;
            }
            case 'checkbox': {
                const newCheckbox = { name: val.name, val: 1 };

                if (val.checked) {
                    if (answers[activeQuestion]) {
                        answersArray =  [...filteredAnswers, newCheckbox];
                    } else {
                        answersArray = [newCheckbox];
                    }
                } else {
                    answersArray = answers[activeQuestion].answer.filter(answer => answer.name !== newCheckbox.name);

                    if (answersArray.length === 0) {
                        answersArray.push({ name: null, val: null });
                    }
                }
                break;
            }
            case 'number':
            case 'text':
            case 'select': {
                const newField = { name: val.name, val: type === 'number' ? Number(val.value) : val.value };

                if (answers[activeQuestion]) {
                    const existingAnswerIndex = answers[activeQuestion].answer.findIndex(answer => answer.name === newField.name);
                    if (existingAnswerIndex !== -1) {
                        answersArray = answers[activeQuestion].answer.map((answer, index) =>
                            index === existingAnswerIndex ? newField : answer
                        );
                    } else {
                        answersArray = [...filteredAnswers, newField];
                    }
                } else {
                    answersArray = [newField]
                }
                break;
            }
        }

        let newAnswer = {
            question_id: question_id,
            answer: answersArray
        };

        saveDataToLocalStorage(localStorageKey, newAnswer, updatedAnswers => {
            dispatch(updateAnswers(updatedAnswers));
        });

        dispatch(setSelected(true));

        const timeout = window.setTimeout(() => {

            if (state.activeQuestion + 1 === state.testData.length) {
                if (!currentQuestionNextBtnShow) {
                    dispatch(setIsFinished(true));
                }
            } else {
                if (!currentQuestionNextBtnShow) {
                    dispatch(setActiveQuestion(state.activeQuestion + 1));
                    dispatch(setSelected(false));
                }
            }
            window.clearTimeout(timeout)
        }, 300)
    }
}